import React, {useState} from 'react'
import { useDispatch } from 'react-redux'
import Dropdown from 'components/forms/Dropdown'
import { statusOptions} from 'constants/constantVariables'
import './applicantDetail.scss'
import chroma from 'chroma-js';
import ConfirmationDialog from "../../ConfirmationDialog";
import { getUserDetailByToken } from 'actions/root';
import { TextField, CircularProgress, Box, RadioGroup, FormControlLabel, Radio, FormControl, FormLabel } from '@material-ui/core';
import AuthService from 'tools/AuthService'
import FlagOutlined from '@material-ui/icons/FlagOutlined'
import DeclineEmailSection, { defaultDeclineEmail, declineEmailErrors, declineEmailFooterNote } from './DeclineEmailSection'

const ApplicationStatusDropdown = (props) => {
    const {
        status,
        dispatchProcessing,
        updateApplicationStatus,
        applicantUUID,
        flaggedBusinessType // business type flagged in the client's settings, or null
    } = props

    const dispatch = useDispatch();
    const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false)
    const [newStatus, setNewStatus] = useState(status)
    const [reasoning, setReasoning] = useState('')
    const [selectedReasonOption, setSelectedReasonOption] = useState('')
    const [decliningApplicationsOffersEnabled, setDecliningApplicationsOffersEnabled] = useState(false)
    const [fetchingUserDetails, setFetchingUserDetails] = useState(false)
    const declineEmailEnabled = AuthService.getClientFeatures().applicationDeclineEmailEnabled
    const [declineEmail, setDeclineEmail] = useState(defaultDeclineEmail())
    const [declineEmailTouched, setDeclineEmailTouched] = useState(false)
    const MAX_REASONING_LENGTH = 500

    const openUpdateStatusDialog = async (value) => {
        if (status !== value){
            setNewStatus(value)
            setReasoning('')
            setSelectedReasonOption('')
            setDeclineEmail(defaultDeclineEmail())
            setDeclineEmailTouched(false)
            if (value === 'Declined') {
                setFetchingUserDetails(true)
                try {
                    const userDetails = await dispatch(getUserDetailByToken())
                    const clientData = userDetails?.securityContext?.client
                    setDecliningApplicationsOffersEnabled(clientData?.decliningApplicationsOffersEnabled || false)
                } catch (error) {
                    setDecliningApplicationsOffersEnabled(false)
                } finally {
                    setFetchingUserDetails(false)
                }
            }

            setChangeStatusDialogOpen(true)
        }
    }

    const updateStatus = (value) => {
        if (value === 'Declined' && decliningApplicationsOffersEnabled && !selectedReasonOption) {
            return;
        }

        if (value === 'Declined' && selectedReasonOption === 'other' && !reasoning.trim()) {
            return;
        }

        const sendDeclineEmail = value === 'Declined' && declineEmailEnabled && declineEmail.send
        if (sendDeclineEmail && Object.keys(declineEmailErrors(declineEmail)).length) {
            setDeclineEmailTouched(true)
            return;
        }

        if (value) {
            dispatchProcessing(true)

            const reasoningText = selectedReasonOption === 'other' ? reasoning.trim() : selectedReasonOption
            const isFraud = selectedReasonOption === 'Fraud Identified (System Alert Flagged)'

            const declineEmailPayload = sendDeclineEmail
                ? { recipients: declineEmail.recipients, subject: declineEmail.subject.trim(), message: declineEmail.message.trim(), replyTo: declineEmail.replyTo }
                : null

            updateApplicationStatus(applicantUUID, value, reasoningText, isFraud, declineEmailPayload).finally(() => {
                dispatchProcessing(false)
                setChangeStatusDialogOpen(false)
                setReasoning('')
                setSelectedReasonOption('')
            })
        }
    }

    const dot = (color = '#1f807d') => ({
        alignItems: 'center',
        display: 'flex',

        ':before': {
            backgroundColor: color,
            borderRadius: 10,
            content: '" "',
            display: 'block',
            marginRight: 8,
            height: 10,
            width: 10,
        },
    });

    const colourStyles = {
        control: styles => ({ ...styles,
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: 0,
            display: 'flex',
            width: '150px',
            minHeight: 32,
            borderRadius: 'var(--radius-md)'}),
        option: (styles, { data, isDisabled, isFocused, isSelected}) => {
            const color = chroma(data.color);
            return {
                ...styles,
                backgroundColor: isDisabled
                    ? null
                    : isSelected
                        ? data.color
                        : isFocused
                            ? color.alpha(0.1).css()
                            : null,
                color: isDisabled
                    ? '#ccc'
                    : isSelected
                        ? chroma.contrast(color, 'white') > 2
                            ? 'white'
                            : 'black'
                        : data.color,
                cursor: isDisabled ? 'not-allowed' : 'default',

                ':active': {
                    ...styles[':active'],
                    backgroundColor: !isDisabled && (isSelected ? data.color : color.alpha(0.3).css()),
                },
            };
        },
        input: styles => ({ ...styles, ...dot() }),
        placeholder: styles => ({ ...styles, ...dot() }),
        singleValue: (styles, { data }) => ({ ...styles, ...dot(data.color) }),
    };

    return (<>
            <Dropdown
                    className={'application-status-dropdown'}
                    placeholder={status}
                    options={statusOptions}
                    onChange={(e) => openUpdateStatusDialog(e?.value)}
                    value={status}
                    isClearable={false}
                    propStyles={colourStyles}
                    customSelect={true}
            />
            <ConfirmationDialog
                open={changeStatusDialogOpen}
                onClose={() => setChangeStatusDialogOpen(false)}
                onConfirm={() => updateStatus(newStatus)}
                title={"Change Application Status"}
                fullWidth={newStatus === 'Declined' && declineEmailEnabled}
                footerNote={newStatus === 'Declined' && declineEmailEnabled ? declineEmailFooterNote(declineEmail, declineEmailTouched) : null}
                message={
                    <div>
                        <p>{`Are you sure you want to change the application status to "${newStatus}"?`}</p>
                        {['Approved', 'Cautionary Approval'].includes(newStatus) && flaggedBusinessType && (
                            <Box display="flex" alignItems="flex-start" style={{ color: 'var(--badge-watch-ink)', gap: 6 }}>
                                <FlagOutlined fontSize='small' aria-hidden='true' style={{ marginTop: 3, flexShrink: 0 }} />
                                <span>{`${flaggedBusinessType} is a flagged business type.`}</span>
                            </Box>
                        )}
                        {fetchingUserDetails && (
                            <Box display="flex" alignItems="center" justifyContent="center" my={2}>
                                <CircularProgress size={24} />
                            </Box>
                        )}
                        {newStatus === 'Declined' && decliningApplicationsOffersEnabled && !fetchingUserDetails && (
                            <FormControl component="fieldset" style={{ marginTop: 16, width: '100%' }}>
                                <FormLabel component="legend">Reason for Decline *</FormLabel>
                                <RadioGroup
                                    value={selectedReasonOption}
                                    onChange={(e) => {
                                        setSelectedReasonOption(e.target.value)
                                        if (e.target.value !== 'other') {
                                            setReasoning('')
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        value="Fraud Identified (System Alert Flagged)"
                                        control={
                                            <Radio
                                                style={{
                                                    color: selectedReasonOption === 'Fraud Identified (System Alert Flagged)' ? '#1f807d' : 'var(--fg-tertiary)',
                                                    '&.Mui-checked': {
                                                        color: '#1f807d'
                                                    }
                                                }}
                                            />
                                        }
                                        label="Fraud Identified (System Alert Flagged)"
                                    />
                                    <FormControlLabel
                                        value="Financial Insolvency"
                                        control={
                                            <Radio
                                                style={{
                                                    color: selectedReasonOption === 'Financial Insolvency' ? '#1f807d' : 'var(--fg-tertiary)',
                                                    '&.Mui-checked': {
                                                        color: '#1f807d'
                                                    }
                                                }}
                                            />
                                        }
                                        label="Financial Insolvency"
                                    />
                                    <FormControlLabel
                                        value="other"
                                        control={
                                            <Radio
                                                style={{
                                                    color: selectedReasonOption === 'other' ? '#1f807d' : 'var(--fg-tertiary)',
                                                    '&.Mui-checked': {
                                                        color: '#1f807d'
                                                    }
                                                }}
                                            />
                                        }
                                        label="Other"
                                    />
                                </RadioGroup>
                                {selectedReasonOption === 'other' && (
                                    <TextField
                                        label="Please specify reason *"
                                        placeholder="Please provide a reason for declining this application..."
                                        multiline
                                        rows={4}
                                        variant="outlined"
                                        fullWidth
                                        value={reasoning}
                                        onChange={(e) => setReasoning(e.target.value)}
                                        inputProps={{ maxLength: MAX_REASONING_LENGTH }}
                                        error={!reasoning.trim()}
                                        helperText={!reasoning.trim() ? "Custom reasoning is required" : `${MAX_REASONING_LENGTH - reasoning.length} characters remaining`}
                                        style={{ marginTop: 16 }}
                                    />
                                )}
                                {!selectedReasonOption && (
                                    <div style={{ color: 'var(--status-declined)', fontSize: '0.75rem', marginTop: 8 }}>
                                        Please select a reason for declining
                                    </div>
                                )}
                            </FormControl>
                        )}
                        {newStatus === 'Declined' && declineEmailEnabled && (
                            <DeclineEmailSection
                                applicationUUID={applicantUUID}
                                value={declineEmail}
                                onChange={setDeclineEmail}
                                submitted={declineEmailTouched}
                            />
                        )}
                    </div>
                }
                confirmText={"Confirm"}
                cancelText={"Cancel"}
            />
        </>
        )
}

export default ApplicationStatusDropdown
